import {WindowSizeConsumer} from '@albert-io/atomic/helpers/WindowSizeProvider/WindowSizeProvider.react';
import cx from 'classnames';
import type {KeyboardEvent} from 'react';
import React, {useRef} from 'react';
import {Popper} from 'react-popper';
import useAutoFocusReturn from 'lib/hooks/useAutoFocusReturn';

import {toModifierArray} from '../../helpers/popper/popperCompat';

import {useMenu} from './MenuContext.react';
import {focusMenuItem, MENU_ITEM_SELECTOR} from './menuKeyboard';

const modifiers = {
  preventOverflow: {
    padding: 64,
    boundariesElement: 'viewport' as const
  },
  flip: {
    padding: 16
  }
};

interface MenuContentProps extends PropsWithChildrenRequired, PropsWithClassNameOptional {}

const MenuContent = ({children, className = ''}: MenuContentProps) => {
  const {isOpen, close, closeOnItemClick, position, positionFixed, popperProps} = useMenu();
  const menuRef = useRef<HTMLDivElement>(null);
  // ARIA menu-button pattern; restore is manual so a mouse click doesn't yank focus back.
  const restoreFocus = useAutoFocusReturn(menuRef, isOpen);

  const closeAndRestoreFocus = () => {
    restoreFocus();
    close();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!menuRef.current) return;

    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        focusMenuItem(menuRef.current, 'next');
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        focusMenuItem(menuRef.current, 'previous');
        break;
      }
      // Tab must be intercepted or it would step through items while the menu
      // stays open. Both leave the menu and restore focus to the trigger;
      // this covers Shift+Tab (same key).
      case 'Tab':
      case 'Escape': {
        e.preventDefault();
        closeAndRestoreFocus();
        break;
      }
      case 'Enter':
      case ' ': {
        if (closeOnItemClick && (e.target as HTMLElement).matches(MENU_ITEM_SELECTOR)) {
          restoreFocus();
        }
        break;
      }
      default:
        break;
    }
  };

  if (!isOpen) return null;

  return (
    <WindowSizeConsumer>
      {(context) => {
        if (!context) return null;

        // popperProps fields override the Menu defaults (matching react-popper 0.10's
        // trailing-spread semantics).
        const effectiveModifiers = popperProps?.modifiers ?? modifiers;
        const effectivePlacement = popperProps?.placement ?? position;
        const effectiveStrategy = popperProps?.strategy ?? (positionFixed ? 'fixed' : 'absolute');

        return (
          <Popper
            placement={effectivePlacement}
            strategy={effectiveStrategy}
            modifiers={toModifierArray(effectiveModifiers)}
          >
            {({ref, style, placement}) => (
              <div
                ref={ref}
                style={style}
                data-placement={placement}
                className={cx('o-menu-content__wrapper', className, {
                  'o-menu-content__wrapper--show': isOpen
                })}
              >
                <div
                  role='menu'
                  className='o-menu-content rounded-x1 border-border-primary background-primary p-x1 shadow-l2 border border-solid'
                  onKeyDown={handleKeyDown}
                  ref={menuRef}
                >
                  {children}
                </div>
              </div>
            )}
          </Popper>
        );
      }}
    </WindowSizeConsumer>
  );
};

MenuContent.displayName = 'MenuContent';

export default MenuContent;
